import { isFeatureEnabled } from '@sdk/utils/isFeatureEnabled';
import { TimesheetStatus } from '@sdk/timesheets/api/apiTypes';
import { TimesheetEntryType } from '@sdk/timesheets/types/TimesheetEntry';

import { TIMESHEETS_TABLE_COLUMN_DESCRIPTIONS } from '@web/timeClock/constants';

import ReportsBaseCtrl from '../reports/ReportsBaseCtrl';
import ReportTableColumns from '../reports/services/ReportTableColumns';
import printButtonComponentModalCtrl from '../components/components/printButtonComponentModalCtrl';
import printButtonComponentModalUrl from '../components/components/printButtonComponentModal.html';
import config from '../config';

import TIMESHEETS_UI_SCHEMA from './timesheetsUISchema';
import timesheetsDataUrl from './timesheets-data.html';

export default class TimesheetsCtrl extends ReportsBaseCtrl {
  tableColumnDescriptions = TIMESHEETS_TABLE_COLUMN_DESCRIPTIONS;
  timesheetsUISchema = TIMESHEETS_UI_SCHEMA;
  reportId = 'timesheets';

  timesheetsDataUrl = timesheetsDataUrl;
  groupByDateEnabled = true;

  constructor(
    Authentication,
    PersonaCollection,
    TimesheetsService,
    Utils,
    KioskService,
    ReportsUtils,
    LaborUISchema,
    reportsPrintPayloadReplacer,
    uppercaseFirstFilter,
    $injector,
    $uibModal,
    $scope,
    $state,
    $rootScope
  ) {
    'ngInject';

    super($injector, $scope);
    Object.assign(this, {
      Authentication,
      PersonaCollection,
      TimesheetsService,
      Utils,
      KioskService,
      ReportsUtils,
      LaborUISchema,
      reportsPrintPayloadReplacer,
      uppercaseFirstFilter,
      $injector,
      $uibModal,
      $scope,
      $state,
      $rootScope,
    });

    this.multiselectOptions = [
      { label: 'All', value: '*' },
      { label: 'Pending approval', value: TimesheetStatus.pending },
      isFeatureEnabled(config.TIMESHEET_CONSENT_FLOW_ENABLED, this.Authentication.user?.org.id)
        ? { label: 'Pending consent', value: TimesheetStatus.pendingConsent }
        : null,
      { label: 'Approved', value: TimesheetStatus.approved },
    ].filter(Boolean);

    this.refreshUserOptions([], this.relevantUsers);
    this.setRefreshWatcher();

    this.initialLoaded = false;
    this.selectedRows = {};

    this.getPrintPayload = this.getPrintPayload.bind(this); // hardbind
    this.initColumns();
    this.$scope.$emit('TimesheetsCtrl:loaded', this);
    this.$scope.$watchCollection('ctrl.selectedRows', () => this.updateSelectedAll());

    this.AUTOMATED_BREAKS_ENABLED = isFeatureEnabled(
      config.AUTOMATED_BREAKS_ENABLED,
      Authentication.user.org.id
    );
  }

  setRefreshWatcher() {
    if (!__HEADLESS__) {
      this.DISABLING_SCHEDULE_AUTOSYNC_ENABLED = Array.isArray(
        config.DISABLING_SCHEDULE_AUTOSYNC_ENABLED
      )
        ? config.DISABLING_SCHEDULE_AUTOSYNC_ENABLED.includes(this.Authentication.user.org.id)
        : config.DISABLING_SCHEDULE_AUTOSYNC_ENABLED;
      this.FORCE_SCHEDULE_AUTOSYNC_OFF = config.FORCE_SCHEDULE_AUTOSYNC_OFF;

      this.$scope.$on('socket:timesheets:sync', () => {
        if (
          this.DISABLING_SCHEDULE_AUTOSYNC_ENABLED &&
          (this.Authentication.user.settings.scheduleAutoSync === false ||
            this.FORCE_SCHEDULE_AUTOSYNC_OFF)
        ) {
          this.timesheetsHavePendingChanges = true;
        } else {
          this.Utils.debounce(this.refresh, 1000);
        }
      });
    }
  }

  initColumns() {
    this.columns = new ReportTableColumns(this.Authentication, 'timesheetsReportColumns');
    this.columns.reset();
  }

  updateSelectedAll() {
    if (__HEADLESS__) {
      return;
    }

    const selectableTimesheets = this.timesheets?.filter((e) => e.canBeSelected())?.length;
    this._allSelected = Boolean(
      selectableTimesheets &&
        Object.values(this.selectedRows).filter(Boolean).length === selectableTimesheets
    );
  }

  multiselectOptionClicked(val) {
    this.multiselectOption = undefined;

    if (!this.timesheets?.length) {
      return;
    }

    if (val === '*') {
      this.allSelected = true;
      return;
    }

    this.selectedRows = {};
    this.timesheets.forEach((event) => {
      if (event.timesheetStatus === val) {
        this.selectedRows[event.id] = true;
      }
    });
  }

  get orgHasTags() {
    return Boolean(this.PersonaCollection.tags.length);
  }

  get orgHasGroups() {
    return Boolean(this.PersonaCollection.groups.length);
  }

  get allSelected() {
    return this._allSelected;
  }

  set allSelected(val) {
    if (val) {
      this.selectedRows = this.timesheets.reduce(
        (acc, e) => ((acc[e.id] = e.canBeSelected()), acc),
        {}
      );
    } else {
      this.selectedRows = {};
    }

    this.updateSelectedAll();
  }

  get isSomethingSelected() {
    return Object.values(this.selectedRows).filter(Boolean).length;
  }

  get isSomethingToSelect() {
    return this.Utils.memoizePerDigest(() => this.timesheets?.some((t) => t.canBeSelected()));
  }

  get isSomethingSelectedToApprove() {
    return Object.entries(this.selectedRows).filter(
      ([id, isSelected]) =>
        isSelected &&
        id in this.timesheetsByEvent &&
        this.timesheetsByEvent[id].timesheetStatus === TimesheetStatus.pending
    ).length;
  }

  async approveSelected() {
    const events = Object.entries(this.selectedRows)
      .filter(
        ([id, isSelected]) =>
          isSelected &&
          id in this.timesheetsByEvent &&
          this.timesheetsByEvent[id].timesheetStatus === TimesheetStatus.pending
      )
      .map(([id]) => this.timesheetsByEvent[id]);

    const ids = events
      .map((event) => event.timesheetProjections)
      .reduce((acc, arr) => acc.concat(arr), [])
      .map((obj) => obj.id);

    if (ids.length) {
      await this.TimesheetsService.approveTimesheets(ids, events);
      this.timesheets = [...this.timesheets]; // trigger table refresh
    }

    this.selectedRows = {};
  }

  updateLocationsPositionsAndGroups() {
    super.updateLocationsPositionsAndGroups();

    if (this.allTimesheets) {
      this.allTimesheets.forEach(this.handleArchivedGroups);
    }
  }

  applyFilters() {
    this.filterTimesheets();
  }

  refresh = async () => {
    this.loading = true;
    const from = this.selectedRange[0];
    const to = this.selectedRange[this.selectedRange.length - 1];
    let fetched = false;
    try {
      const allTimesheets = await this.Utils.raceProofRequest(
        this.TimesheetsService.getTimesheets(from, to),
        this.trackRaceProofRequest(from, to)
      );

      fetched = true;

      if (!this.isRangeStillSelected(from, to)) {
        if (this.ownsLoadingState(from, to)) {
          this.loading = false;
        }

        return;
      }

      this.allTimesheets = allTimesheets;
      this.handleTimesheetSources();
      this.timesheetsHavePendingChanges = false;
      this.filterTimesheets();
      this.updateLocationsPositionsAndGroups();
      this.refreshUserOptions(
        this.allTimesheets.map((t) => t.user),
        this.relevantUsers
      );
      this.initialLoaded = true;
      this.selectedRows = {};
      this._allSelected = false;
      this.loading = false;
    } catch (e) {
      if (this.isSupersededRequestError(e)) {
        return;
      }

      if (!fetched && this.isRangeStillSelected(from, to)) {
        // The heading already shows this range; the previous range's rows must
        // not stay under it (RF-35753). Only a failed fetch blanks the report —
        // a render error means the data arrived fine and still matches.
        this.allTimesheets = null;
        this.timesheets = [];
      }

      if (this.ownsLoadingState(from, to)) {
        this.loading = false;
      }

      throw e;
    }
  };

  handleTimesheetSources() {
    const sources = this.Utils.uniq(this.allTimesheets.map((t) => t.fromIntegration));

    if (sources.length <= 1) {
      this.showShiftsSource = false;
      this.sourceOptions = [];
      return;
    }
 
    this.showShiftsSource = true;
    this.sourceOptions = sources.sort().map((source) => ({
      name: this.uppercaseFirstFilter(source),
      id: source,
    }));

    this.hasSyncErrors = this.allTimesheets.some((t) => !!t.syncErrors);
  }

  isShownByStatusFilter(item) {
    if (!this.filter.statusIds?.length) {
      return true;
    }

    if (
      this.filter.statusIds.includes(this.NONE_STATUS) &&
      [TimesheetStatus.inProgress, undefined].includes(item.timesheetStatus)
    ) {
      return true;
    }

    return this.filter.statusIds.includes(item.timesheetStatus);
  }

  isShownByIsAutoClockOutFilter(item) {
    if (!this.filter.isAutoClockOutIds?.length) {
      return true;
    }

    return item.isAutoClockOutArray.some(({ id: idInArray }) => {
      return this.filter.isAutoClockOutIds.some((id) => id === idInArray);
    });
  }

  filterTimesheets() {
    if (!this.allTimesheets) {
      return;
    }

    this.timesheets = this.allTimesheets.filter((item) => {
      const shownByStatusFilter = this.isShownByStatusFilter(item);
      const shownBySourceFilter =
        !this.showShiftsSource ||
        !this.filter.sourceIds?.length ||
        this.filter.sourceIds.includes(item.fromIntegration);
      const shownByIsAutoClockOutFilter = this.isShownByIsAutoClockOutFilter(item);

      return (
        shownByStatusFilter &&
        shownBySourceFilter &&
        shownByIsAutoClockOutFilter &&
        this.ReportsUtils.isEntityShownByFilters(this.filter, item)
      );
    });

    this.timesheetsByEvent = this.timesheets.reduce(
      (acc, event) => ((acc[event.id] = event), acc),
      {}
    );

    this.detectTypesOfBreaksInView();
    this.updateSelectedAll();
  }

  _prepareBulkPrint = () =>
    Object.values(
      this.timesheets.reduce((agg, t) => {
        if (!agg[t.user.id]) {
          agg[t.user.id] = [];
        }

        agg[t.user.id].push(t);
        return agg;
      }, {})
    ).map((v) => this.getPrintPayload(v, v[0].user.fullname));

  printForAllEmployees() {
    this.$uibModal
      .open({
        size: '500',
        templateUrl: printButtonComponentModalUrl,
        controllerAs: 'ctrl',
        controller: printButtonComponentModalCtrl,
        resolve: {
          prepare: () => this._prepareBulkPrint,
          args: () => {},
          type: () => 'labor',
          title: () => 'Print timesheets',
          caption: () => 'Print a separate PDF for every employee on the list',
          batchPrint: () => true,
        },
      })
      .result.ignoreCancel();
  }

  getPrintPayload(timesheetsOverride, filenameSuffix) {
    const props = [
      'timesheets',
      '_selectedDate',
      'tableOrderBy',
      '_sortBy',
      '_rangeMode',
      'filter',
      '_selectedRange',
      'showDurationsAsDecimals',
      'collapsedGroups',
      'showShiftsSource',
    ];

    const timesheetsCtrl = this.Utils.extendWithProps(
      this,
      {
        rangeStart: this.Utils.formatDate(this.rangeStart),
        rangeEnd: this.Utils.formatDate(this.rangeEnd),
      },
      props
    );

    if (timesheetsOverride) {
      timesheetsCtrl.timesheets = timesheetsOverride;
    }

    const authenticationUser = {
      settings: {
        usTimeFormat: this.Authentication.user.settings.usTimeFormat,
        clientFlags: {
          timesheetsReportColumns:
            this.Authentication.user.settings.clientFlags.timesheetsReportColumns,
        },
      },
      hasAccessToLaborCost: this.Authentication.user.hasAccessToLaborCost,
      wouldHaveAccessToLaborCost: this.Authentication.user.wouldHaveAccessToLaborCost,
      managedEmployees: this.Authentication.user.managedEmployees,
      isAdmin: this.Authentication.user.isAdmin,
      isManager: this.Authentication.user.isManager,
      org: {
        isPremium: this.Authentication.user.org.isPremium,
      },
    };

    authenticationUser.orgSettings = { ...this.Authentication.user.orgSettings };
    delete authenticationUser.orgSettings.shiftTemplates;

    const allPersonas = this.PersonaCollection.allPersonas.map((persona) =>
      this.Utils.extendWithProps(
        persona,
        {
          locations: persona.locations?.map(({ id }) => ({ id })),
        },
        ['id', 'type', 'name', 'color', 'fullname', 'employeeId']
      )
    );

    return {
      timesheetsCtrl,
      authenticationUser,
      allPersonas,
      tags: this.PersonaCollection.tags.map(({ id, name }) => ({ id, name })),
      currencySymbol: this.$rootScope.currencySymbol,

      $route: 'reports/timesheets',
      $replacer: this.reportsPrintPayloadReplacer,
      $filenameSuffix: filenameSuffix,
    };
  }

  canDataColumnBeVisible = (column) => {
    const options = {
      sortBy: this.sortBy,
      tags: this.PersonaCollection.tags,
      groups: this.PersonaCollection.groups,
      showShiftsSource: this.showShiftsSource,
      timesheetsWithBreaksExistInView: this.timesheetsWithBreaksExistInView,
    };

    const descriptor = this.timesheetsUISchema?.table.items.find(
      (item) => item.property === column
    );
    return !descriptor || this.LaborUISchema.evalIf(descriptor, options);
  };

  get relevantUsers() {
    return this.PersonaCollection.users.filter((u) => u.timeclockEnabled);
  }

  async addNewTimesheet() {
    let defaultDate = this.Utils.formatDate(this.Utils.getNowDate());

    if (!this.selectedRange.includes(defaultDate)) {
      defaultDate = this.selectedRange[0];
    }

    const { visibleHoursStart, visibleHoursEnd } = this.Authentication.user.orgSettings;

    await this.TimesheetsService.viewTimesheet({
      defaultDate: this.Utils.getBusinessDayForCalendar(
        visibleHoursStart,
        visibleHoursEnd,
        defaultDate
      ).visibleStart,
    });

    this.refresh();
  }

  detectTypesOfBreaksInView() {
    this.timesheetsWithBreaksExistInView = false;

    this.timesheets.forEach((shift) => {
      if (this.timesheetsWithBreaksExistInView) {
        return;
      }

      shift.timesheetEntries.forEach((entry) => {
        if (this.timesheetsWithBreaksExistInView) {
          return;
        }

        if (entry.type === TimesheetEntryType.breakStart) {
          this.timesheetsWithBreaksExistInView = true;
        }
      });
    });
  }
}
